import React from 'react';
import PropTypes from 'prop-types';
import withStyles from '@material-ui/core/styles/withStyles';
import { IconButton, Tooltip } from '@material-ui/core';

import NumberRule from './rules/NumberRule';
import SelectRule from './rules/SelectRule';
import MultiSelectRule from './rules/MultiSelectRule';
import AreaRule from './rules/AreaRule';
import LayerRule from './rules/LayerRule';

import DeleteIcon from '@material-ui/icons/Delete';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';

import { Autocomplete } from '@material-ui/lab';
import TextField from '../../../../../Common/UI/TextField';
import { v4 as uuidv4 } from 'uuid';
import { compose } from 'redux';
import withSearchTree from './hoc/WithSearchTree';
import DateRule from './rules/DateRule';
import StringRule from './rules/StringRule';
import IntersectsRule from './rules/IntersectsRule';
import WithinRule from './rules/WithinRule';
import MultiStringRule from './rules/MultiStringRule';

const DEFAULT_CONFIG = {
    types: {
        select: {
            defaultProperties: {
                operator: '=='
            }
        },
        multiselect: {
            defaultProperties: {
                operator: '=='
            }
        },
        area: {
            defaultProperties: {
                operator: 'between'
            }
        },
        integer: {
            defaultProperties: {
                operator: 'between'
            }
        },
        date: {
            defaultProperties: {
                operator: 'between'
            }
        },
        string: {
            defaultProperties: {
                operator: 'ilike'
            }
        },
        multistring: {
            defaultProperties: {
                operator: 'ilike'
            }
        }
    }
};

class Rule extends React.Component {
    static propTypes = {
        classes: PropTypes.object.isRequired,
        tree: PropTypes.object.isRequired,
        fields: PropTypes.object.isRequired,
        // HOC - WithSearchTree
        depth: PropTypes.number.isRequired,
        onBranch: PropTypes.func.isRequired,
        onChange: PropTypes.func.isRequired,
        onDelete: PropTypes.func.isRequired,
        onAddRule: PropTypes.func.isRequired, // Not used currently
        onAddGroup: PropTypes.func.isRequired, // Not used currently
        onConjunctionChange: PropTypes.func.isRequired // Not used currently
    };

    constructor(props) {
        super(props);

        this.handleAddSubRuleClick = this.handleAddSubRuleClick.bind(this);
        this.getRule = this.getRule.bind(this);
        this.handleRuleChange = this.handleRuleChange.bind(this);
        this.filterRules = this.filterRules.bind(this);
        this.sortRules = this.sortRules.bind(this);
    }

    handleAddSubRuleClick() {
        const { tree, fields, onChange } = this.props;
        const newTree = { ...tree };

        newTree.type = 'group-rule';
        newTree.conjunction = 'and';
        newTree.children = [
            ...(newTree.children || []),
            {
                id: uuidv4(),
                type: 'rule',
                field: null,
                isValid: false
            }
        ];

        onChange(newTree);
    }

    getRule() {
        const { tree, fields, onChange } = this.props;
        const field = { ...fields[tree.field] };

        if (field.settingsInherit) {
            field.settings = { ...fields[field.settingsInherit].settings };
        }

        switch (field.type) {
            case 'intersects':
            case 'notintersects':
                return (
                    <IntersectsRule tree={tree} field={field} onChange={onChange} />
                );
            case 'within':
            case 'notwithin':
                return <WithinRule tree={tree} field={field} onChange={onChange} />;
            // Layer rule is Legacy but still works
            case 'layer':
                return <LayerRule tree={tree} field={field} onChange={onChange} />;
            case 'integer':
                return <NumberRule tree={tree} field={field} onChange={onChange} />;
            case 'area':
                return <AreaRule tree={tree} field={field} onChange={onChange} />;
            case 'select':
                return <SelectRule tree={tree} field={field} onChange={onChange} />;
            case 'multiselect':
                return (
                    <MultiSelectRule tree={tree} field={field} onChange={onChange} />
                );
            case 'date':
                return <DateRule tree={tree} field={field} onChange={onChange} />;
            case 'string':
                return <StringRule tree={tree} field={field} onChange={onChange} />;
            case 'multistring':
                return (
                    <MultiStringRule tree={tree} field={field} onChange={onChange} />
                );
            default:
                return field.label || tree.field;
        }
    }

    handleRuleChange(event, value) {
        const { tree, fields, onChange } = this.props;
        const isRuleRequired =
            fields[value]?.isRequired && tree.id.includes('required');

        if (isRuleRequired) return;

        const newTree = { ...tree };
        newTree.field = value;
        newTree.type = 'rule';
        delete newTree.conjunction;
        delete newTree.children;
        newTree.properties = {};
        if (value) {
            if (fields[value]) {
                const ruleConfig = DEFAULT_CONFIG.types[fields[value].type];
                if (ruleConfig && ruleConfig.defaultProperties) {
                    newTree.properties = { ...ruleConfig.defaultProperties };
                }
            }
        }

        onChange(newTree);
    }

    // Easy way to ensure something still shows in config but don't wont to show in list
    // Layers that are autogenerated will be hidden and are referenced by the spatial rules such as intersects and within
    filterRules(key) {
        const { fields, depth } = this.props;
        // Don't show spatial queries more than 1 depth in to prevent endless chains
        if (depth > 1) {
            switch (fields[key].type) {
                case 'intersects':
                case 'notintersects':
                case 'within':
                case 'notwithin':
                    return false;
                default:
                    break;
            }
        }
        return !fields[key].hidden;
    }

    sortRules(a, b) {
        const { fields } = this.props;
        const categoryA = fields[a].category || '';
        const categoryB = fields[b].category || '';
        if (categoryA < categoryB) {
            return -1;
        }
        if (categoryA > categoryB) {
            return 1;
        }
        if (a < b) {
            return -1;
        }
        if (a > b) {
            return 1;
        }
        return 0;
    }

    render() {
        const { classes, tree, fields, depth, onBranch, onDelete } = this.props;

        const field = fields[tree.field] ?? [];
        const isRuleRequired = field?.isRequired && tree.id.includes('required');
        const ruleRequiredInfo = `${field?.label} is a required field. If you would like to search using an ‘or’ operation please use the group function.`;

        let hasSubfields = false;
        // Only allow one level of subfields, prevents layers chaining subfields via spatial queries
        if (depth < 2) {
            if (tree.field) {
                switch (tree.field) {
                    case 'intersects':
                    case 'notintersects':
                    case 'within':
                    case 'notwithin':
                        // ToDo check layer exists and fetch fields for layers at root or special source
                        hasSubfields = Boolean(
                            tree.properties.layer &&
                                this.props.fields[tree.properties.layer].subfields
                        );
                        break;
                    default:
                        hasSubfields = Boolean(fields[tree.field].subfields);
                }
            }
        }

        // const hasSubfields = tree.field && fields[tree.field].subfields;

        const options = Object.keys(fields)
            .filter(this.filterRules)
            .sort(this.sortRules);

        return (
            <>
                <div className={classes.root}>
                    <div className={classes.ruleContainer}>
                        <Autocomplete
                            renderInput={params => (
                                <TextField
                                    inputRef={params.InputProps.ref}
                                    inputProps={params.inputProps}
                                    size="small"
                                    placeholder="Add Criteria"
                                />
                            )}
                            getOptionLabel={option => fields[option].label}
                            options={options}
                            groupBy={option => fields[option].category}
                            value={tree.field || null}
                            onChange={this.handleRuleChange}
                            disableClearable
                            disabled={isRuleRequired}
                        />
                        {hasSubfields && (
                            <IconButton
                                aria-label="Add Sub Rule"
                                onClick={this.handleAddSubRuleClick}
                                size="small">
                                <AddCircleIcon />
                            </IconButton>
                        )}
                        {isRuleRequired ? (
                            <Tooltip title={ruleRequiredInfo}>
                                <IconButton aria-label="Delete Rule" size="small">
                                    <InfoOutlinedIcon />
                                </IconButton>
                            </Tooltip>
                        ) : (
                            <IconButton
                                aria-label="Delete Rule"
                                onClick={onDelete}
                                size="small">
                                <DeleteIcon />
                            </IconButton>
                        )}
                    </div>
                    <div className={classes.propertiesContainer}>
                        {tree.field && this.getRule()}
                    </div>
                </div>
                {tree.children && tree.children.length > 0 && (
                    <div className={classes.children}>
                        {tree.children.map(onBranch)}
                    </div>
                )}
            </>
        );
    }
}

const style = theme => ({
    root: {
        backgroundColor: theme.palette.background.main,
        padding: 8,
        borderRadius: 5
    },
    ruleContainer: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        '&> *': {
            margin: 2
        },
        '&> button:first-of-type': {
            marginLeft: 'auto'
        }
    },
    propertiesContainer: {
        display: 'flex',
        '&> *': {
            margin: 2
        }
    },
    children: {
        padding: '0px 0px 0px 20px',
        '&> *:not(:last-child)': {
            marginBottom: 10
        },
        '&> *:first-child': {
            marginTop: 10
        }
    }
});

export default compose(withStyles(style), withSearchTree)(Rule);
